import { OpenAPI } from '@/client';
import type {
  ChapterCreate,
  ChapterDetailResponse,
  ChapterPublic,
  ChapterUpdate,
  ChaptersPublic,
  ComicDetailResponse,
  ComicPublicList,
  ComicListComicsData,
  ComicsPublic,
  ComicUpdate,
  Message,
  Pagination_ChannelSourceDeviceEntity_,
  PresignedUrlRequest,
  PresignedUrlResponse,
  TagCreate,
  TagDetail,
  TagsPublic,
  TagUpdate,
} from '@/client/types.gen';

export type ComicTagOverrides = {
  csid: string;
  upstream_tags: Array<{ code: string; name: string; import_keys?: string[] }>;
  available_tags: Array<{ code: string; name: string; import_keys: string[] }>;
  added_tags: string[];
  blocked_tags: string[];
};

export type AdminComicListRow = Omit<ComicPublicList, 'tags'> & {
  tags?: Array<
    NonNullable<ComicPublicList['tags']>[number] & { import_keys?: string[] }
  >;
  tag_overrides: Omit<ComicTagOverrides, 'available_tags'>;
  click_count?: number;
  tag_review_not_applicable?: boolean;
  layout?: string | null;
  manual_cover?: { url?: string; width?: number; height?: number } | null;
};

const token = async () =>
  typeof OpenAPI.TOKEN === 'function'
    ? OpenAPI.TOKEN({} as never)
    : OpenAPI.TOKEN || '';

const queryString = (values: Record<string, unknown>) => {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const text = params.toString();
  return text ? `?${text}` : '';
};

export const adminRequest = async <T>(
  path: string,
  init: RequestInit = {},
): Promise<T> => {
  const authToken = await token();
  const response = await fetch(`${OpenAPI.BASE}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      Authorization: authToken ? `Bearer ${authToken}` : '',
      ...init.headers,
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((payload as { detail?: string }).detail || '请求失败');
  }
  return payload as T;
};

export const ComicService = {
  getAdminConfig: () => adminRequest<Record<string, unknown>>('/api/v2/config'),
  listComics: (
    data: ComicListComicsData & {
      layout?: string;
      excludedTagIds?: number[];
      hasManualTags?: boolean;
      manualTag?: string;
      maintenanceTag?: string;
      hideNotApplicable?: boolean;
    } = {},
  ) =>
    adminRequest<Omit<ComicsPublic, 'data'> & { data: AdminComicListRow[] }>(
      `/api/v2/comics${queryString({
        skip: data.skip,
        limit: data.limit,
        comic_id: data.comicId,
        csid: data.csid,
        title: data.title,
        author: data.author,
        serial_status: data.serialStatus,
        tag_keyword: data.tagKeyword,
        tag_id: data.tagId,
        has_manual_tags: data.hasManualTags,
        manual_tag: data.manualTag,
        maintenance_tag: data.maintenanceTag,
        hide_not_applicable: data.hideNotApplicable,
        excluded_tag_ids: data.excludedTagIds?.length
          ? data.excludedTagIds.join(',')
          : undefined,
        source_site: data.sourceSite,
        language: data.language,
        status: data.status,
        is_copyright: data.isCopyright,
        is_finished: data.isFinished,
        is_ai_translated: data.isAiTranslated,
        safe: data.safe,
        priority: data.priority,
        include_images: data.includeImages,
        layout: data.layout,
        sort_by: data.sortBy,
        sort_order: data.sortOrder,
      })}`,
    ),
  getComic: ({ comicId }: { comicId: number }) =>
    adminRequest<
      Omit<ComicDetailResponse, 'comic'> & { comic: AdminComicListRow }
    >(`/api/v2/comics/${comicId}`),
  updateComic: ({
    comicId,
    requestBody,
  }: {
    comicId: number;
    requestBody: ComicUpdate;
  }) =>
    adminRequest<ComicPublicList>(`/api/v2/comics/${comicId}`, {
      method: 'PUT',
      body: JSON.stringify(requestBody),
    }),
  deleteComic: ({ comicId }: { comicId: number }) =>
    adminRequest<Message>(`/api/v2/comics/${comicId}`, { method: 'DELETE' }),
  setComicTagReview: (
    comicIds: number[],
    targetTag: string,
    notApplicable: boolean,
  ) =>
    adminRequest<{ affected_comics: number }>(
      '/api/v2/comics/tag_review/batch',
      {
        method: 'POST',
        body: JSON.stringify({
          comic_ids: comicIds,
          target_tag: targetTag,
          not_applicable: notApplicable,
        }),
      },
    ),
  batchAddComicTags: (comicIds: number[], addedTags: string[]) =>
    adminRequest<{ affected_comics: number; affected_csids: number }>(
      '/api/v2/comics/tag_overrides/batch',
      {
        method: 'POST',
        body: JSON.stringify({ comic_ids: comicIds, added_tags: addedTags }),
      },
    ),
  getComicTagOverrides: (comicId: number) =>
    adminRequest<ComicTagOverrides>(`/api/v2/comics/${comicId}/tag_overrides`),
  updateComicTagOverrides: (
    comicId: number,
    rules: Pick<ComicTagOverrides, 'added_tags' | 'blocked_tags'>,
  ) =>
    adminRequest<Message>(`/api/v2/comics/${comicId}/tag_overrides`, {
      method: 'PUT',
      body: JSON.stringify(rules),
    }),
  updateComicTags: ({
    comicId,
    requestBody,
  }: {
    comicId: number;
    requestBody: number[];
  }) =>
    adminRequest<Message>(`/api/v2/comics/${comicId}/tags`, {
      method: 'PUT',
      body: JSON.stringify(requestBody),
    }),
  updateComicPriority: ({
    comicId,
    priority,
  }: {
    comicId: number;
    priority: number;
  }) =>
    adminRequest<ComicPublicList>(
      `/api/v2/comics/${comicId}/priority?priority=${priority}`,
      { method: 'PUT' },
    ),
  listChapters: (
    data: {
      skip?: string | number;
      limit?: string | number;
      comicId?: number;
      languageCode?: string;
      status?: string;
      sortBy?: string;
    } = {},
  ) =>
    adminRequest<ChaptersPublic>(
      `/api/v2/chapters${queryString({
        skip: data.skip,
        limit: data.limit,
        comic_id: data.comicId,
        language_code: data.languageCode,
        status: data.status,
        sort_by: data.sortBy,
      })}`,
    ),
  getChapter: ({ chapterId }: { chapterId: number }) =>
    adminRequest<ChapterDetailResponse>(`/api/v2/chapters/${chapterId}`),
  createChapter: ({ requestBody }: { requestBody: ChapterCreate }) =>
    adminRequest<ChapterPublic>('/api/v2/chapters', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    }),
  updateChapter: ({
    chapterId,
    requestBody,
  }: {
    chapterId: number;
    requestBody: ChapterUpdate;
  }) =>
    adminRequest<ChapterPublic>(`/api/v2/chapters/${chapterId}`, {
      method: 'PUT',
      body: JSON.stringify(requestBody),
    }),
  deleteChapter: ({ chapterId }: { chapterId: number }) =>
    adminRequest<Message>(`/api/v2/chapters/${chapterId}`, {
      method: 'DELETE',
    }),
};

export const TagService = {
  listTags: (data: Record<string, unknown> = {}) =>
    adminRequest<TagsPublic>(
      `/api/v2/tags${queryString({
        skip: data.skip,
        limit: data.limit,
        status: data.status,
        keyword: data.name,
        source_site: data.sourceSite,
        is_recommended: data.isRecommended,
        is_copyright: data.isCopyright,
        hidden: data.hidden,
        show_in_category: data.showInCategory,
        sort_by: data.sortBy,
        sort_order: data.sortOrder,
      })}`,
    ),
  createTag: ({ requestBody }: { requestBody: TagCreate }) =>
    adminRequest<TagDetail>('/api/v2/tags', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    }),
  updateTag: ({
    tagId,
    requestBody,
  }: {
    tagId: number;
    requestBody: TagUpdate;
  }) =>
    adminRequest<TagDetail>(`/api/v2/tags/${tagId}`, {
      method: 'PUT',
      body: JSON.stringify(requestBody),
    }),
  deleteTag: ({ tagId }: { tagId: number }) =>
    adminRequest<Message>(`/api/v2/tags/${tagId}`, { method: 'DELETE' }),
  updateTagHidden: ({ tagId, hidden }: { tagId: number; hidden: boolean }) =>
    adminRequest<TagDetail>(`/api/v2/tags/${tagId}/hidden?hidden=${hidden}`, {
      method: 'PUT',
    }),
};

export const UploadService = {
  getPresignedUrl: ({ requestBody }: { requestBody: PresignedUrlRequest }) =>
    adminRequest<PresignedUrlResponse>('/api/v2/upload/presigned-url', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    }),
};

export const BlackService = {
  listSourceDevices: (data: Record<string, unknown> = {}) =>
    adminRequest<Pagination_ChannelSourceDeviceEntity_>(
      `/api/v2/source-devices${queryString({
        skip: data.skip,
        limit: data.limit,
        platform: data.platform,
        source: data.source,
        device_id: data.deviceId,
      })}`,
    ),
};
